import React, { useCallback, useState, useEffect } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { toast } from 'sonner';
import { Building2, Plus, Loader2 } from 'lucide-react';
import { API } from '../App';

const PlaidLinkButton = ({ onSuccess, className = '' }) => {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [plaidConfigured, setPlaidConfigured] = useState(true);

  // Check Plaid status on mount
  useEffect(() => {
    const checkPlaidStatus = async () => {
      try {
        const response = await API.get('/plaid/status');
        setPlaidConfigured(response.data.configured);
      } catch (error) {
        console.error('Error checking Plaid status:', error);
        setPlaidConfigured(false);
      }
    };
    checkPlaidStatus();
  }, []);

  // Get link token from backend
  const initializePlaid = async () => {
    if (!plaidConfigured) {
      toast.error('Bank connections are not configured. Please add Plaid API credentials.');
      return;
    }
    
    setLoading(true);
    try {
      const response = await API.post('/plaid/create-link-token');
      setLinkToken(response.data.link_token);
    } catch (error) {
      console.error('Error creating link token:', error);
      toast.error(error.response?.data?.detail || 'Failed to initialize bank connection');
      setLoading(false);
    }
  };

  // Handle successful connection
  const handleSuccess = useCallback(async (publicToken, metadata) => {
    try {
      const response = await API.post('/plaid/exchange-token', {
        public_token: publicToken,
        institution_name: metadata.institution.name,
        institution_id: metadata.institution.institution_id,
      });

      toast.success(`Connected to ${metadata.institution.name}!`);
      
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      console.error('Error exchanging token:', error);
      toast.error('Failed to complete bank connection');
    }
  }, [onSuccess]);

  // Handle exit (user closed modal)
  const handleExit = useCallback((err, metadata) => {
    setLinkToken(null);
    setLoading(false);
    
    if (err) {
      console.error('Plaid Link error:', err);
    }
  }, []);

  // Plaid Link configuration
  const config = {
    token: linkToken,
    onSuccess: handleSuccess,
    onExit: handleExit,
  };

  const { open, ready } = usePlaidLink(config);

  // When link token is ready, open Plaid Link
  useEffect(() => {
    if (linkToken && ready) {
      open();
    }
  }, [linkToken, ready, open]);

  // Reset loading when link token changes
  useEffect(() => {
    if (linkToken) {
      setLoading(false);
    }
  }, [linkToken]);

  if (!plaidConfigured) {
    return (
      <button
        disabled
        className={`flex items-center gap-2 px-5 py-3 rounded-lg bg-slate-700 text-slate-400 font-semibold cursor-not-allowed ${className}`}
        title="Plaid not configured - Add API credentials to enable"
      >
        <Building2 className="h-5 w-5" />
        Bank Connection (Not Configured)
      </button>
    );
  }

  return (
    <button
      onClick={initializePlaid}
      disabled={loading}
      className={`flex items-center gap-2 px-5 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold transition-all disabled:opacity-50 ${className}`}
    >
      {loading ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin" />
          Connecting...
        </>
      ) : (
        <>
          <Plus className="h-5 w-5" />
          Connect Bank Account
        </>
      )}
    </button>
  );
};

export default PlaidLinkButton;
